import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {HTMLElement[]} Elements made inert while the main drawer is open */
  #inertElements = [];

  /** Page scroll position when the main drawer opened */
  #scrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#setPageInert(false);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    const isMainDrawer = details === this.refs.details;
    if (isMainDrawer) this.#placeDrawer(summary);

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // The drawer is a modal dialog: nothing outside it can be reached while it is open
      if (isMainDrawer) this.#setPageInert(true);

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          // Trap focus in the panel itself: the menu button stays under the panel and must not be reachable
          if (isMainDrawer) {
            trapFocus(this.refs.menuDrawer);
            this.refs.menuDrawer.querySelector('button')?.focus({ preventScroll: true });
          } else {
            trapFocus(details);
          }
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        // A hidden panel drops focus to the body, so both cases mean the keyboard user was in the drawer
        const focusWasInDrawer =
          this.refs.menuDrawer.contains(document.activeElement) || document.activeElement === document.body;
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          this.#setPageInert(false);
          this.#restoreScroll();
          // Give focus back to the menu button that opened the drawer
          if (focusWasInDrawer) summary.focus({ preventScroll: true });
        } else {
          trapFocus(this.refs.menuDrawer);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Record where the drawer starts and where its close button goes: the sheet begins below the header,
   * and the close button takes the menu button's place, so the header itself stays in view.
   * @param {HTMLElement} summary - The menu button
   */
  #placeDrawer(summary) {
    this.#scrollY = window.scrollY;

    const drawer = this.refs.menuDrawer;
    const headerSection = this.closest('.header-section') ?? this.closest('#header-component');
    const headerBottom = headerSection ? headerSection.getBoundingClientRect().bottom : 0;
    const toggle = summary.getBoundingClientRect();
    const closeSize = 44;

    drawer.style.setProperty('--sustena-drawer-top', `${Math.max(0, Math.round(headerBottom))}px`);
    drawer.style.setProperty('--sustena-drawer-toggle-top', `${Math.round(toggle.top + (toggle.height - closeSize) / 2)}px`);
    drawer.style.setProperty('--sustena-drawer-toggle-left', `${Math.round(toggle.left + (toggle.width - closeSize) / 2)}px`);
  }

  /**
   * Put back the scroll position the page had when the drawer opened.
   */
  #restoreScroll() {
    const scrollY = this.#scrollY;
    requestAnimationFrame(() => {
      if (Math.abs(window.scrollY - scrollY) > 1) window.scrollTo({ top: scrollY, behavior: 'instant' });
    });
  }

  /**
   * Make everything outside the drawer inert (or restore it), by walking up from the drawer and marking
   * the siblings of each ancestor. The backdrop stays interactive so a click on it still closes the drawer.
   * @param {boolean} inert
   */
  #setPageInert(inert) {
    if (!inert) {
      for (const element of this.#inertElements) element.inert = false;
      this.#inertElements = [];
      return;
    }

    if (this.#inertElements.length) return;

    const skip = new Set(['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE']);
    /** @type {Element | null} */
    let node = this.refs.menuDrawer;

    while (node && node !== document.body && node.parentElement) {
      for (const sibling of node.parentElement.children) {
        if (sibling === node || !(sibling instanceof HTMLElement)) continue;
        if (skip.has(sibling.tagName) || sibling.inert || sibling.classList.contains('menu-drawer__backdrop')) continue;
        sibling.inert = true;
        this.#inertElements.push(sibling);
      }
      node = node.parentElement;
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
